import { useMemo, useState, type ReactNode } from 'react';

import { Button } from '../../shared/ui/button';
import { FieldLabel } from '../../shared/ui/field-label';
import { Hint } from '../../shared/ui/hint';
import { PageHeader } from '../../shared/ui/layout';
import { Note } from '../../shared/ui/note';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { InfoDot } from '../../shared/ui/tooltip';
import { isDraft, type IPin } from '../../entities/transform';
import {
  EMPTY_PROMPT,
  PromptMessages,
  parseSample,
  promptReaderData,
  sharedProblems,
  variablesOf,
  type TLibraryItem,
  type TLibraryMessage,
  type TLibraryVersionSummary,
} from '../../entities/library';
import { useSession } from '../../entities/session';
import { LibraryCompare } from '../../features/library-compare';
import { LibraryDelete } from '../../features/library-delete';
import { PromptTest, type IPromptTestSample } from '../../features/library-prompt-test';
import { LibraryPublish } from '../../features/library-publish';
import { LibraryVersions } from '../../features/library-versions';
import { TemplateInput } from '../../features/flow-template-input';
import { UsedInFlows } from '../../widgets/used-in-flows';
import { headerFacts } from './code-item';
import { useVersionBody } from './use-library-item';

const same = (a: readonly TLibraryMessage[], b: readonly TLibraryMessage[]): boolean =>
  a.length === b.length &&
  a.every((message, i) => message.role === b[i].role && message.content === b[i].content);

export const PromptItemScreen = ({
  item,
  versions,
  pins,
  breadcrumb,
  onDeleted,
}: {
  item: TLibraryItem;
  versions: TLibraryVersionSummary[];
  pins: IPin[];
  breadcrumb: ReactNode;
  onDeleted: () => void;
}) => {
  const [selected, setSelected] = useState(item.latestVersion);
  const [comparing, setComparing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // Null: showing what the server has. A list: somebody edited it.
  const [edit, setEdit] = useState<TLibraryMessage[] | null>(null);
  const [sample, setSample] = useState<IPromptTestSample>({ eventText: '', vars: {} });

  const timeZone = useSession().data?.preferences.timezone;
  const now = useMemo(() => Date.now(), [item.id, item.updatedAt]);
  const body = useVersionBody(item.id, selected > 0 ? selected : null);
  const saved = selected > 0 ? (body.data?.messages ?? null) : EMPTY_PROMPT;
  const messages = edit ?? saved ?? EMPTY_PROMPT;
  const dirty = edit !== null && (saved === null || !same(edit, saved));
  const unsaved = item.latestVersion === 0;

  const problems = useMemo(() => sharedProblems(messages), [messages]);
  const variables = useMemo(() => variablesOf(messages), [messages]);
  const event = useMemo(() => parseSample(sample.eventText).value, [sample.eventText]);
  const reader = useMemo(
    () => promptReaderData({ event, trigger: null, vars: sample.vars }, variables),
    [event, sample.vars, variables],
  );
  const firstProblem = problems.values().next().value ?? null;

  const show = (version: number) => {
    setSelected(version);
    setEdit(null);
  };

  return (
    <>
      <PageHeader
        title={item.name}
        breadcrumbs={breadcrumb}
        badge={
          <span className="flex items-center gap-2 font-mono text-micro text-faint">
            prompt · {unsaved ? 'no version yet' : `v${selected}`}
            {!unsaved && isDraft(selected, item.latestVersion, pins) ? ' · draft' : ''}
            {dirty ? ' · edited' : ''}
            <InfoDot side="bottom">
              A version runs only where an AI step in a flow pins it. Publishing changes nothing on
              its own.
            </InfoDot>
          </span>
        }
        description={headerFacts(item, pins, now, timeZone)}
        actions={
          <>
            <LibraryDelete item={item} onDeleted={onDeleted} />
            <Button
              size="sm"
              disabled={!(dirty || unsaved) || problems.size > 0}
              onClick={() => setPublishing(true)}
            >
              Publish v{item.latestVersion + 1}
            </Button>
          </>
        }
      />

      <div className="flex w-full max-w-content items-start gap-5 p-page">
        <div className="flex min-w-0 flex-[1.5] flex-col gap-2">
          <FieldLabel>Messages</FieldLabel>
          {body.isError && <ErrorState error={body.error} onRetry={() => void body.refetch()} />}
          {selected > 0 && body.isPending && <LoadingState rows={8} />}
          {(selected === 0 || body.data) && (
            <PromptMessages
              key={selected}
              idPrefix={`prompt-${item.id}`}
              messages={messages}
              onChange={setEdit}
              problems={problems}
              renderContent={(props) => (
                <TemplateInput
                  id={props.id}
                  aria-label={props.label}
                  value={props.value}
                  onChange={props.onChange}
                  invalid={props.invalid}
                  multiline
                  data={reader}
                  sampleName={sample.eventText.trim() ? 'the sample event' : undefined}
                />
              )}
            />
          )}
          <Hint>
            A message reads {'{{event.*}}'}, {'{{trigger.*}}'} and its own variables, like{' '}
            {'{{vars.customer}}'}; each step that uses the prompt binds every variable to data of
            its flow. At most one system message, first; the last one is from the user.
          </Hint>
          {dirty && !unsaved && (
            <Note>
              Edited against v{selected}, not saved. Publishing writes v{item.latestVersion + 1};
              picking another version on the right discards it.
            </Note>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-5">
          {versions.length > 0 && (
            <LibraryVersions
              item={item}
              versions={versions}
              pins={pins}
              selected={selected}
              onSelect={show}
              onCompare={() => setComparing(true)}
              now={now}
            />
          )}
          <PromptTest
            messages={messages}
            variables={variables}
            sample={sample}
            onSample={setSample}
            blocked={firstProblem}
          />
          <UsedInFlows
            kind="transform"
            id={item.id}
            name={item.name}
            timeZone={timeZone}
            emptyText="No AI step uses this prompt."
          />
        </div>
      </div>

      {versions.length > 1 && (
        <LibraryCompare
          // A new version resets the pair to "newest against the one before".
          key={versions[0]?.version}
          item={item}
          versions={versions}
          pins={pins}
          open={comparing}
          onOpenChange={setComparing}
        />
      )}
      <LibraryPublish
        item={item}
        body={{ messages: messages.map(({ role, content }) => ({ role, content })) }}
        basedOn={selected}
        pins={pins}
        open={publishing}
        onOpenChange={setPublishing}
        onPublished={show}
      />
    </>
  );
};
